import { BehaviorSubject, Subject, debounceTime, distinctUntilChanged } from 'rxjs';

export abstract class BaseViewModel<TState> {
  readonly state$: BehaviorSubject<TState>;
  readonly events$ = new Subject<{ kind: string; payload?: unknown }>();
  protected destroyed = false;

  constructor(initial: TState) {
    this.state$ = new BehaviorSubject<TState>(initial);
  }

  /**
   * Crea un disparador con `debounceTime` + `distinctUntilChanged` para inputs
   * de búsqueda que recargan del server: evita un request por tecla y descarta
   * valores repetidos. El `handler` corre con el último valor tras `ms` de
   * inactividad. La suscripción es interna y se libera con el VM al desmontar
   * (useViewModel no llama destroy; ver su nota).
   *
   * @example
   * private readonly searchReload = this.debouncedReload(() => { this.patch({ page: 1 }); void this.load(); });
   * setSearch(s: string) { this.patch({ search: s }); this.searchReload(s); }
   */
  protected debouncedReload(handler: (value: string) => void, ms = 300): (value: string) => void {
    const subject = new Subject<string>();
    subject.pipe(debounceTime(ms), distinctUntilChanged()).subscribe(handler);
    return (value: string) => subject.next(value);
  }

  protected patch(partial: Partial<TState>): void {
    if (this.destroyed) return;
    this.state$.next({ ...this.state$.value, ...partial });
  }

  protected emit(kind: string, payload?: unknown): void {
    if (this.destroyed) return;
    this.events$.next({ kind, payload });
  }

  destroy(): void {
    this.destroyed = true;
    this.state$.complete();
    this.events$.complete();
  }
}
